import React, { useState } from 'react';
import DocumentTitle from 'react-document-title';
import { TableHeader } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { Tabs } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import {
  Box,
  Button,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { PlusIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import TableLayout from '../../components/TableLayout';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useModal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import CreateReportModal from './components/CreateCentralizedReportModal';
import CreateScheduledReportModal from './components/CreateScheduledReportModal';
import ViewHistoryModal from './components/ViewCentralizedReportsHistoryModal';
import ReportsTab from './components/ReportsTab';
import ScheduledReportsTab from './components/ScheduledReportsTab';
import {
  useManageCentralizedReports,
  useManageScheduledReports,
  usePrefetchSchedulingReports,
} from './hooks';
import messages from './messages';
import useFormatMessage from '../../hooks/useFormatMessage';
import { NAME, SCHEDULED_REPORTS_NAME } from './constants';

const TABS = [
  {
    label: 'Reports',
    value: 'reports',
  },
  {
    label: 'Scheduled Reports',
    value: 'scheduled-reports',
  },
];

function AllManageReports({
  intl,
  organizationId,
  userId,
  username,
  userProperties,
}) {
  // Faz prefetch dos reports para o modal de agendamento quando a pagina carrega
  usePrefetchSchedulingReports({
    organizationId,
  });

  const [selectedTab, setSelectedTab] = useState('reports');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportForModal, setReportForModal] = useState(null);
  const [newlyGeneratedReports, setNewlyGeneratedReports] = useState([]);
  const [scheduleForEdit, setScheduleForEdit] = useState(null);
  const formatMessage = useFormatMessage();
  const TITLE = formatMessage(messages.manageReports);
  const {
    closeModal: closeCreateReportModal,
    openModal: openCreateReportModal,
    open: createReportModalIsOpen,
  } = useModal(false);
  const {
    closeModal: closeViewHistoryModal,
    openModal,
    open: viewHistoryModalIsOpen,
  } = useModal(false);
  const {
    closeModal: closeScheduleReportModal,
    openModal: openScheduleReportModal,
    open: scheduleReportModalIsOpen,
  } = useModal(false);

  function openViewHistoryModal(report) {
    setSelectedReport(report);
    openModal();
  }

  function handleSelectReportForCreate(row) {
    setReportForModal(row);
    openCreateReportModal();
  }

  function handleClearReportForModal() {
    setReportForModal(null);
  }

  function handleQueueReport() {
    if (!reportForModal) return;
    setNewlyGeneratedReports((prev) => [...prev, reportForModal]);
  }

  function handleScheduleCreated() {
    setScheduleForEdit(null);
    closeScheduleReportModal();
  }

  function handleScheduleUpdated() {
    setScheduleForEdit(null);
    closeScheduleReportModal();
  }

  function handleViewEditSchedule(schedule) {
    setScheduleForEdit(schedule);
    openScheduleReportModal();
  }

  function handleCloseScheduleModal() {
    setScheduleForEdit(null);
    closeScheduleReportModal();
  }

  const { projectNames, totalCount, count } = useManageCentralizedReports({
    NAME,
    organizationId,
    userId,
    username,
    newlyGeneratedReports,
    setNewlyGeneratedReports,
  });

  const { totalCount: scheduledReportsTotalCount } = useManageScheduledReports({
    NAME: SCHEDULED_REPORTS_NAME,
    organizationId,
    userId,
    username,
  });

  return (
    <DocumentTitle title={TITLE}>
      <>
        <TableLayout name={NAME} childrenElementsHeight={50 + 8 + 48}>
          {(tableHeight) => (
            <>
              <TableHeader
                title={TITLE}
                totalCount={
                  selectedTab === 'reports'
                    ? totalCount
                    : scheduledReportsTotalCount
                }
                count={
                  selectedTab === 'reports' ? count : scheduledReportsTotalCount
                }
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Tabs
                  tabs={TABS}
                  value={selectedTab}
                  sx={{ borderBottom: 'none' }}
                  onChange={(_, value) => setSelectedTab(value)}
                />
                {selectedTab === 'scheduled-reports' && (
                  <Button
                    variant="primarySubtle"
                    startIcon={<PlusIcon />}
                    onClick={openScheduleReportModal}
                  >
                    {formatMessage(messages.addNewSchedule)}
                  </Button>
                )}
              </Box>
              {selectedTab === 'reports' ? (
                <ReportsTab
                  organizationId={organizationId}
                  userId={userId}
                  username={username}
                  onSelectReport={handleSelectReportForCreate}
                  openViewHistoryModal={openViewHistoryModal}
                  tableHeight={tableHeight}
                  newlyGeneratedReports={newlyGeneratedReports}
                  setNewlyGeneratedReports={setNewlyGeneratedReports}
                />
              ) : null}
              {selectedTab === 'scheduled-reports' ? (
                <ScheduledReportsTab
                  organizationId={organizationId}
                  userId={userId}
                  username={username}
                  tableHeight={tableHeight}
                  onViewEdit={handleViewEditSchedule}
                />
              ) : null}
            </>
          )}
        </TableLayout>
        <CreateReportModal
          isOpen={createReportModalIsOpen}
          closeModal={closeCreateReportModal}
          reportName={reportForModal?.name}
          version={reportForModal?.version}
          zip={reportForModal?.zip}
          reportId={reportForModal?.id}
          organizationId={organizationId}
          projectNames={projectNames}
          configuration={reportForModal?.configuration}
          userProperties={userProperties}
          onReportQueued={handleQueueReport}
          onResetSelectedReport={handleClearReportForModal}
        />
        <ViewHistoryModal
          isOpen={viewHistoryModalIsOpen}
          closeModal={closeViewHistoryModal}
          intl={intl}
          organizationId={organizationId}
          report={selectedReport}
        />
        <CreateScheduledReportModal
          isOpen={scheduleReportModalIsOpen}
          closeModal={handleCloseScheduleModal}
          organizationId={organizationId}
          userId={userId}
          userProperties={userProperties}
          onScheduleCreated={handleScheduleCreated}
          schedule={scheduleForEdit}
          onScheduleUpdated={handleScheduleUpdated}
        />
      </>
    </DocumentTitle>
  );
}

const mapStateToProps = (state) => {
  const {
    app: { currentUser },
  } = state;
  return {
    organizationId: currentUser?.user?.organization?.id ?? '',
    userId: currentUser?.user?.id ?? '',
    username: currentUser?.user?.username ?? '',
    userProperties: currentUser?.user?.properties ?? [],
  };
};

export default connect(mapStateToProps)(injectIntl(AllManageReports));
